import { createStyles, type Theme } from '@material-ui/core/styles';
import { cockpitType } from './cockpitTokens';

const journeyListStyles = (theme: Theme) =>
  createStyles({
    artifactGroups: { display: 'grid', gap: theme.spacing(3) },
    artifactGroup: { display: 'grid', gap: theme.spacing(1) },
    // the phase name labels the list below it, in the same tier as a metric label
    artifactGroupTitle: {
      ...cockpitType.eyebrow,
      color: theme.palette.text.secondary,
      margin: 0,
    },
    artifactReferenceLine: {
      marginBottom: theme.spacing(2),
      overflowWrap: 'anywhere',
    },
    // no gap: the connector has to run unbroken for the list to read as a
    // sequence rather than as three unrelated cards
    artifactSequence: {
      display: 'grid',
      gap: 0,
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    artifactSequenceItem: {
      position: 'relative',
      minWidth: 0,
      paddingLeft: theme.spacing(3.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': { borderBottom: 0 },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: theme.spacing(1.5),
        top: 0,
        bottom: 0,
        width: 1,
        background: theme.palette.divider,
      },
      '&:first-child::before': { top: theme.spacing(2.5) },
      '&:last-child::before': {
        bottom: `calc(100% - ${theme.spacing(2.5)}px)`,
      },
    },
  });

// the marker and the selected rule share a JSS reference, so they stay together
const journeyMarkerStyles = (theme: Theme) =>
  createStyles({
    artifactSequenceMarker: {
      position: 'absolute',
      zIndex: 1,
      left: theme.spacing(1),
      top: theme.spacing(2.125),
      width: 9,
      height: 9,
      borderRadius: '50%',
      border: `2px solid ${theme.palette.background.paper}`,
      background: theme.palette.text.secondary,
      boxShadow: `0 0 0 1px ${theme.palette.divider}`,
    },
    // closed, an artifact is a row on the rail; open, it becomes the document
    // container it now holds, so it takes a frame of its own
    artifactSequenceSelected: {
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: theme.shape.borderRadius,
      background: theme.palette.background.paper,
      margin: theme.spacing(1, 0),
      '& $artifactSequenceMarker': {
        background: theme.palette.secondary.main,
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      },
    },
  });

const journeyHeaderStyles = (theme: Theme) =>
  createStyles({
    artifactSequenceButton: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      width: '100%',
      border: 0,
      padding: theme.spacing(1.25, 1.5),
      textAlign: 'left',
      background: 'transparent',
      color: theme.palette.text.primary,
      cursor: 'pointer',
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.primary.main}`,
        outlineOffset: -3,
      },
    },
    // opaque on purpose: a translucent sticky header lets the document show
    // through while it scrolls underneath
    artifactSequenceButtonOpen: {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      background: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    },
    artifactSequenceHeading: { margin: 0, font: 'inherit' },
    artifactSequenceLines: { display: 'grid', gap: 2, minWidth: 0 },
    artifactSequencePosition: {
      ...cockpitType.eyebrow,
      color: theme.palette.text.secondary,
    },
    artifactSequenceName: { fontWeight: 600 },
    artifactSequenceToggle: {
      flex: 'none',
      color: theme.palette.secondary.dark,
      fontWeight: 600,
      textTransform: 'none',
    },
  });

const journeyContentStyles = (theme: Theme) =>
  createStyles({
    // no inner scroller: the page is the only scrolling surface
    artifactOpenBody: { padding: theme.spacing(2.5, 1.5, 3) },
    // 76ch at the theme's 14px body resolves to ~592px, below the 640px floor
    // this task requires; rem keeps the measure honest under user font scaling
    artifactReadingColumn: {
      maxWidth: '44rem',
      margin: '0 auto',
      minWidth: 0,
    },
    artifactRecordStrip: {
      display: 'flex',
      flexWrap: 'wrap',
      margin: 0,
      background: theme.palette.action.hover,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& > div': {
        flex: '1 1 170px',
        // Material-UI v4 has no global border-box reset; without this a
        // percentage or grid track would not absorb padding and border
        boxSizing: 'border-box',
        minWidth: 0,
        padding: theme.spacing(1, 1.5, 1.25),
        borderRight: `1px solid ${theme.palette.divider}`,
      },
      '& > div:last-child': { borderRight: 0 },
      '& dt': {
        fontSize: '0.6875rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
      },
      '& dd': { margin: 0, fontSize: '0.8125rem', overflowWrap: 'anywhere' },
      // flex basis arithmetic collapsed to a single column at 390 px; grid
      // states the two columns outright, with nothing to subtract
      [theme.breakpoints.down('xs')]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
    },
  });

export const artifactJourneyStyles = (theme: Theme) =>
  createStyles({
    ...journeyListStyles(theme),
    ...journeyMarkerStyles(theme),
    ...journeyHeaderStyles(theme),
    ...journeyContentStyles(theme),
  });
